;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();import type { ElementType, HTMLAttributes, ReactNode } from "react"
import type { RichTextField } from "../../../lib/content/legacyBlockTypes"
import { cn } from "../../../../lib/utils"
import LandingRichText from "./LandingRichText"

export const landingContainerClass =
  "mx-auto w-full max-w-[1125px] px-[10px] min-[401px]:px-4"

type LandingSectionTone = "default" | "soft" | "accent"
type LandingSectionSpacing = "compact" | "default" | "large" | "hero"
type LandingHeadingSize = "compact" | "section" | "hero"

const sectionToneClasses: Record<LandingSectionTone, string> = {
  default: "bg-white text-[#092c3c]",
  soft: "bg-[#f7fcfc] text-[#092c3c]",
  accent: "bg-[#00b2ff] text-[#092c3c]",
}

const sectionSpacingClasses: Record<LandingSectionSpacing, string> = {
  compact: "py-8 min-[760px]:py-10",
  default: "py-10 min-[760px]:py-14",
  large: "py-12 min-[760px]:py-16",
  hero: "pb-10 pt-8 min-[800px]:pb-14 min-[800px]:pt-12",
}

const headingSizeClasses: Record<LandingHeadingSize, string> = {
  compact: "text-[28px] min-[700px]:text-[34px]",
  section: "text-[34px] min-[700px]:text-[42px]",
  hero: "text-[50px] min-[700px]:text-[76px]",
}

export function LandingContainer({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return <div className={cn(landingContainerClass, className)}>{children}</div>
}

export function LandingSection({
  children,
  className,
  innerClassName,
  spacing = "default",
  tone = "default",
  ...props
}: HTMLAttributes<HTMLElement> & {
  children: ReactNode
  innerClassName?: string
  spacing?: LandingSectionSpacing
  tone?: LandingSectionTone
}) {
  return (
    <section
      className={cn(
        "w-full",
        sectionToneClasses[tone],
        sectionSpacingClasses[spacing],
        className,
      )}
      {...props}
    >
      <LandingContainer className={innerClassName}>{children}</LandingContainer>
    </section>
  )
}

export function LandingEyebrow({
  children,
  className,
}: {
  children?: ReactNode
  className?: string
}) {
  if (!children) return null

  return (
    <p
      className={cn(
        "mb-2 mt-0 text-[13px] font-bold uppercase tracking-normal text-[#147a89]",
        className,
      )}
    >
      {children}
    </p>
  )
}

export function LandingHeading({
  as,
  children,
  className,
  size = "section",
}: {
  as?: ElementType
  children?: ReactNode
  className?: string
  size?: LandingHeadingSize
}) {
  if (!children) return null

  const Component = as || "h2"

  return (
    <Component
      className={cn(
        "m-0 font-bebas font-medium leading-[0.96] tracking-normal text-[#092c3c]",
        size !== "hero" && "leading-tight",
        headingSizeClasses[size],
        className,
      )}
    >
      {children}
    </Component>
  )
}

export function LandingHeadingBlock({
  align = "left",
  body,
  bodyClassName,
  children,
  className,
  eyebrow,
  heading,
  headingAs,
  headingClassName,
  headingSize = "section",
}: {
  align?: "left" | "center"
  body?: RichTextField | null
  bodyClassName?: string
  children?: ReactNode
  className?: string
  eyebrow?: string | null
  heading?: string | null
  headingAs?: ElementType
  headingClassName?: string
  headingSize?: LandingHeadingSize
}) {
  return (
    <div
      className={cn(
        "flex flex-col",
        align === "center" ? "items-center text-center" : "items-start text-left",
        className,
      )}
    >
      <LandingEyebrow>{eyebrow}</LandingEyebrow>
      <LandingHeading as={headingAs} size={headingSize} className={headingClassName}>
        {heading}
      </LandingHeading>
      <LandingRichText
        field={body}
        className={cn(
          "mt-4",
          align === "center" ? "max-w-[760px]" : "max-w-[820px]",
          bodyClassName,
        )}
      />
      {children}
    </div>
  )
}

export function LandingPanel({
  as,
  children,
  className,
  tone = "default",
  ...props
}: HTMLAttributes<HTMLElement> & {
  as?: ElementType
  children: ReactNode
  tone?: "default" | "soft"
}) {
  const Component = as || "div"

  return (
    <Component
      className={cn(
        "rounded-[8px] border p-5 shadow-[0_1px_0_rgba(9,44,60,0.04)]",
        tone === "soft"
          ? "border-[#d7ecef] bg-[#f7fcfc]"
          : "border-[#d7ecef] bg-white",
        className,
      )}
      {...props}
    >
      {children}
    </Component>
  )
}

export function LandingCardTitle({
  as,
  children,
  className,
}: {
  as?: ElementType
  children?: ReactNode
  className?: string
}) {
  if (!children) return null

  const Component = as || "h3"

  return (
    <Component
      className={cn(
        "m-0 text-[20px] font-bold leading-snug tracking-normal text-[#092c3c]",
        className,
      )}
    >
      {children}
    </Component>
  )
}

export function LandingCardGrid({
  children,
  className,
  columns = "three",
}: {
  children: ReactNode
  className?: string
  columns?: "two" | "three"
}) {
  return (
    <div
      className={cn(
        "grid gap-4 min-[760px]:gap-5",
        columns === "two"
          ? "min-[760px]:grid-cols-2"
          : "min-[760px]:grid-cols-2 min-[1040px]:grid-cols-3",
        className,
      )}
    >
      {children}
    </div>
  )
}

export function LandingCountLabel({
  children,
  className,
}: {
  children?: ReactNode
  className?: string
}) {
  if (!children) return null

  return (
    <p
      className={cn(
        "m-0 inline-flex min-h-[28px] items-center rounded-full bg-[#e6ff63] px-3 text-[14px] font-bold leading-tight text-[#092c3c]",
        className,
      )}
    >
      {children}
    </p>
  )
}

export const landingPrimaryButtonClass =
  "inline-flex min-h-[44px] max-w-full items-center justify-center rounded-[6px] bg-[#092c3c] px-5 py-2.5 text-center text-[16px] font-bold leading-tight text-white no-underline transition hover:bg-[#0f4056] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#092c3c]"

export const landingSecondaryButtonClass =
  "inline-flex min-h-[44px] max-w-full items-center justify-center rounded-[6px] border border-[#092c3c] bg-white px-5 py-2.5 text-center text-[16px] font-bold leading-tight text-[#092c3c] no-underline transition hover:bg-[#f4fbfb] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#092c3c]"

export const landingChipLinkClass =
  "inline-flex min-h-[40px] max-w-full items-center rounded-[6px] border border-[#d7ecef] bg-white px-4 py-2 text-[16px] font-bold leading-tight text-[#092c3c] no-underline transition hover:border-[#092c3c] hover:bg-[#f7fcfc] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#092c3c]"

export const landingLinkCardClass =
  "block rounded-[8px] border border-[#d7ecef] bg-white p-5 text-[18px] font-bold leading-snug tracking-normal text-[#092c3c] no-underline transition hover:border-[#092c3c] hover:bg-[#f7fcfc] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#092c3c]"
